import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { Box, Flex } from '@chakra-ui/react';
import {
  HeadingText,
  BodyText,
  LabelText,
  Tag,
  Link,
} from '@spotoninc/nexus-react';
import { CheckIcon } from '@spotoninc/nexus-web-assets';
import { format } from 'date-fns';
import { useSubscriptionStatus } from './useSubscriptionStatus';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import { cancelSubscription } from './cancellationService';
import { getBlid } from '../../utils/cookieHelpers';
import { trackEvent } from '../../utils/analytics';

const DANGER_LINK_COLOR =
  'var(--semantic-color-text-danger-strong-none, #ba2c30)';
const MUTED_TEXT_COLOR =
  'var(--semantic-color-text-neutral-lightest-none, #37456c)';
const CHECK_COLOR =
  'var(--semantic-color-text-neutral-strongest-none, #0a091b)';
const CARD_BG =
  'var(--semantic-color-container-neutral-lightest-none, #f4f7fa)';

// Key features shown on the plan, laid out as two columns of three per Figma.
const KEY_FEATURES = [
  [
    'Automatically flag costly errors',
    'Uncover hidden opportunities for savings',
    'Get proactive emails with actionable insights',
  ],
  [
    'Automated cost categorization',
    'Real-time margin analysis',
    'Inventory waste tracking',
  ],
];

const formatDay = value => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const day = new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  );
  // "Ends on MMM d, yyyy" per the Subscription & Billing design (node 19535:9212).
  return format(day, 'MMM d, yyyy');
};

// Ordinal suffix for a day-of-month (1 -> "1st", 22 -> "22nd", 11 -> "11th").
const ordinal = n => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

// Recurring monthly billing day from a charge date, e.g. "18th of each month".
const formatBillingDay = value => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${ordinal(parsed.getUTCDate())} of each month`;
};

// One label/value cell of the detail list: medium label above a plain value.
const DetailCell = ({ label, value }) => (
  <Box pr="16px" py="8px" width="100%">
    <LabelText as="p" size="medium">
      {label}
    </LabelText>
    <BodyText as="p">{value}</BodyText>
  </Box>
);

// A single "Key features" list item with a check icon in a round button frame.
const FeatureItem = ({ children }) => (
  <Flex align="center" gap="8px" pr="16px" py="8px">
    <Flex
      align="center"
      justify="center"
      p="3px"
      borderRadius="9999px"
      flexShrink={0}
    >
      <CheckIcon size="18px" color={CHECK_COLOR} aria-hidden />
    </Flex>
    <BodyText as="span">{children}</BodyText>
  </Flex>
);

/**
 * "Subscription & Billing" page (PROF-1191). Renders the self-serve plan info
 * plus the cancel flow for both the trial and paid-subscription variants.
 *
 * The route in `drawer.jsx` renders this unconditionally, so eligibility is
 * enforced here: the page fails closed and renders nothing unless the
 * experience is enabled (feature flag), the status has finished loading, and
 * the merchant is in the self-serve cohort. This prevents the hook's inert
 * empty status (isSelfServe/isTrialing false) — surfaced when the flag is off,
 * during the initial fetch, or for a non-self-serve merchant — from rendering
 * as an active paid plan with a live cancellation control.
 */
const SubscriptionBilling = () => {
  const status = useSubscriptionStatus();
  const { isEnabled, refresh } = status;
  const email = useSelector(state => state.user?.email);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // A successful cancellation calls refresh(), flipping the hook to
  // { isLoading: true } with eligibility cleared until the re-read settles.
  // Rendering that inert status would trip the fail-closed guard below and
  // unmount the whole subtree mid-flow — including the open
  // CancelSubscriptionModal, discarding its success screen (isDone /
  // effectiveEndsAt) and re-arming a second cancellation POST when it remounts
  // on the confirm step. Snapshot the last resolved, eligible status and keep
  // rendering it — but ONLY while a refresh is actively in flight for the SAME
  // merchant. Once the refresh settles unsuccessfully (404/error → emptyStatus)
  // or ineligible (non-self-serve), or the merchantKey changes, we drop the
  // snapshot and fall back through to the fail-closed guard so a stale enabled
  // cancel action / another merchant's details never linger.
  const isResolved = isEnabled && !status.isLoading && status.isSelfServe;
  // Snapshot only the fields this page consumes rather than the whole hook
  // return. The hook spreads a fresh object every render, so holding that
  // reference would re-run the effect each render and couple the snapshot to
  // the hook's return shape; a plain owned object keyed off primitive deps
  // fires only on a real status change.
  const lastResolvedRef = useRef(null);
  useEffect(() => {
    if (isResolved) {
      lastResolvedRef.current = {
        isTrialing: status.isTrialing,
        isCancelled: status.isCancelled,
        trialEndDate: status.trialEndDate,
        chargeDate: status.chargeDate,
        pricePerLocation: status.pricePerLocation,
        merchantKey: status.merchantKey,
      };
    }
  }, [
    isResolved,
    status.isTrialing,
    status.isCancelled,
    status.trialEndDate,
    status.chargeDate,
    status.pricePerLocation,
    status.merchantKey,
  ]);
  const snapshot = lastResolvedRef.current;
  // Reuse the snapshot only for an active same-merchant loading state; any
  // settled state (resolved or not) renders the live status and no snapshot.
  const isRefreshingSameMerchant =
    isEnabled &&
    status.isLoading &&
    !!snapshot &&
    snapshot.merchantKey === status.merchantKey;
  const view = isResolved ? status : isRefreshingSameMerchant ? snapshot : null;

  const {
    isTrialing,
    isCancelled,
    trialEndDate,
    chargeDate,
    pricePerLocation,
  } = view ?? {};

  const trialEndLabel = useMemo(() => formatDay(trialEndDate), [trialEndDate]);
  const priceLabel = `$${pricePerLocation}/mo per location`;
  // The cancel modal shows the trial end date when trialing; paid cancellations
  // reference the next charge date instead.
  const modalEndDate = isTrialing ? trialEndDate : chargeDate;

  const handleConfirm = useCallback(
    async ({ reasons, detail }) => {
      const blid = getBlid();
      const subscriptionType = isTrialing ? 'trial' : 'paid';
      // PROF-1193: FE supplies subscriptionType, verbatim reasons[], and optional
      // detail. Actor identity is captured server-side from the Okta session, so
      // no email is sent. Return the response so the modal can read
      // effectiveEndsAt for its success screen.
      const result = await cancelSubscription(blid, {
        subscriptionType,
        reasons,
        detail,
      });
      trackEvent('subscription_cancel_requested', {
        state: subscriptionType,
        reasons,
      });
      // Re-read the authoritative status so the disabled cancel link and the
      // suppressed trial banner reflect the cancellation without a reload. The
      // modal keeps its own success screen (driven by the response), so this
      // background refresh doesn't disturb it.
      refresh();
      return result;
    },
    [isTrialing, refresh]
  );

  // Fail closed: render nothing until the experience is enabled and a self-serve
  // status has resolved at least once. Once resolved, `view` falls back to the
  // last resolved snapshot during a background refresh (see above), so this no
  // longer blanks the page or unmounts the open modal mid-cancellation. Placed
  // after all hooks so the early return doesn't change hook order between renders.
  if (!isEnabled || !view) return null;

  const planName = isTrialing ? 'Free trial' : 'Profit Assist';
  const subtitle = isTrialing
    ? 'Billed monthly after trial ends \u2022 Cancel anytime'
    : 'Billed monthly \u2022 Cancel anytime';
  const cancelLabel = isTrialing ? 'Cancel trial' : 'Cancel subscription';
  const dateLabel = isTrialing ? 'Free trial' : 'Billing date';
  const dateValue = isTrialing
    ? trialEndLabel
      ? `Ends on ${trialEndLabel}`
      : null
    : formatBillingDay(chargeDate);

  return (
    <Flex direction="column" gap="40px" px="40px" py="40px" width="100%">
      <HeadingText as="h1" size="large">
        Subscription &amp; Billing
      </HeadingText>

      {/* Billing header: plan info (left) + account details (right). */}
      <Flex gap="24px" align="flex-start" width="100%" wrap="wrap">
        {/* Left column: plan name + Active tag, subtitle, key features.
            Grows at a 13:7 ratio vs the right column so it takes ~65% and the
            right details card ~35% of the row when both fit on one line. */}
        <Flex direction="column" gap="32px" flex="13 1 320px" minW="280px">
          <Flex direction="column" gap="12px">
            {/* The paid view (Figma 20506:7485) drops the plan name + Active
                tag; only the trial view surfaces them. */}
            {isTrialing && (
              <Flex align="center" gap="16px">
                <HeadingText as="h2" size="small">
                  {planName}
                </HeadingText>
                <Tag variant="info">Active</Tag>
              </Flex>
            )}
            <BodyText as="p" size="small" style={{ color: MUTED_TEXT_COLOR }}>
              {subtitle}
            </BodyText>
          </Flex>

          <Flex direction="column" gap="16px">
            <LabelText as="p" size="large">
              Key features
            </LabelText>
            <Flex gap="8px" wrap="wrap">
              {KEY_FEATURES.map((column, columnIndex) => (
                <Flex
                  key={`feature-col-${columnIndex}`}
                  direction="column"
                  flex="1 1 240px"
                  minW="220px"
                >
                  {column.map(feature => (
                    <FeatureItem key={feature}>{feature}</FeatureItem>
                  ))}
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Flex>

        {/* Right column: account details in a rounded card + cancel link.
            ~35% of the row via the 7 (of 13:7) flex-grow. */}
        <Flex
          direction="column"
          flex="7 1 320px"
          minW="280px"
          bg={CARD_BG}
          borderRadius="12px"
          p="24px"
        >
          {dateValue && <DetailCell label={dateLabel} value={dateValue} />}
          <DetailCell label="Locations" value="1" />
          <DetailCell label="Pricing" value={priceLabel} />
          {email && <DetailCell label="Account email" value={email} />}

          <Box pt="16px">
            {/* Already-cancelled trials/subs retain access until the end date
                but can't be cancelled again, so the link is disabled. */}
            <Link
              as="button"
              type="button"
              isDisabled={isCancelled}
              style={isCancelled ? undefined : { color: DANGER_LINK_COLOR }}
              onClick={() => {
                if (isCancelled) return;
                setIsModalOpen(true);
              }}
            >
              {cancelLabel}
            </Link>
          </Box>
        </Flex>
      </Flex>

      <CancelSubscriptionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
        isTrialing={isTrialing}
        endDate={modalEndDate}
      />
    </Flex>
  );
};

export default SubscriptionBilling;
